import * as React from "react";
import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    CheckboxVisibility,
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    PrimaryButton,
    Checkbox,
    IconButton,
    TextField
} from "@fluentui/react";
import CustomDatePicker from "../../base/CustomDatePicker";
import { RootState } from "../../../state";
import { getEurocodeExceptions } from "../../../state/eurocodeExceptions";
import {
    loadEurocodeExceptions,
    saveEurocodeException,
    removeEurocodeException,
    updateEurocodeExceptionLocal,
    addEurocodeExceptionOptimistic,
    deleteEurocodeExceptionOptimistic
} from "../../../state/eurocodeExceptions/action";
import "./style.css";

let nextTempId = 1;

function formatDate(iso: string): string {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
}

const EurocodeExceptions: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const exceptions = useSelector((state: RootState) => getEurocodeExceptions(state.eurocodeExceptions));

    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValues, setEditValues] = useState<Partial<EurocodeException>>({});

    useEffect(() => {
        dispatch(loadEurocodeExceptions() as any);
    }, []);

    const startEdit = useCallback((row: EurocodeException) => {
        setEditingId(row.id);
        setEditValues({
            euroCode: row.euroCode,
            registrationDate: row.registrationDate,
            bookingsAllowedAfterRegistration: row.bookingsAllowedAfterRegistration
        });
    }, []);

    const cancelEdit = useCallback(() => {
        if (editingId && editingId.startsWith("new_")) {
            dispatch(deleteEurocodeExceptionOptimistic(editingId));
        }
        setEditingId(null);
        setEditValues({});
    }, [editingId, dispatch]);

    const confirmEdit = useCallback(() => {
        const existing = exceptions.find(e => e.id === editingId);
        if (!existing) return;
        const updated: EurocodeException = {
            ...existing,
            euroCode: editValues.euroCode !== undefined ? editValues.euroCode : existing.euroCode,
            registrationDate: editValues.registrationDate || existing.registrationDate,
            bookingsAllowedAfterRegistration:
                editValues.bookingsAllowedAfterRegistration !== undefined
                    ? editValues.bookingsAllowedAfterRegistration
                    : existing.bookingsAllowedAfterRegistration
        };
        dispatch(saveEurocodeException(updated) as any);
        setEditingId(null);
        setEditValues({});
    }, [editingId, editValues, exceptions, dispatch]);

    const addNew = useCallback(() => {
        const tempId = `new_${nextTempId++}`;
        const newRow: EurocodeException = {
            id: tempId,
            euroCode: "",
            registrationDate: new Date().toISOString(),
            bookingsAllowedAfterRegistration: false
        };
        dispatch(addEurocodeExceptionOptimistic(newRow));
        setEditingId(tempId);
        setEditValues({
            euroCode: "",
            registrationDate: new Date().toISOString(),
            bookingsAllowedAfterRegistration: false
        });
    }, [dispatch]);

    const deleteRow = useCallback((id: string) => {
        dispatch(removeEurocodeException(id) as any);
    }, [dispatch]);

    const columns: IColumn[] = [
        {
            key: "index",
            name: "#",
            fieldName: "index",
            minWidth: 30,
            maxWidth: 50,
            onRender: (_item: EurocodeException, index?: number) => <span>{(index !== undefined ? index + 1 : "")}</span>
        },
        {
            key: "euroCode",
            name: "Eurocode",
            fieldName: "euroCode",
            minWidth: 180,
            maxWidth: 300,
            onRender: (row: EurocodeException) =>
                editingId === row.id ? (
                    <TextField
                        value={editValues.euroCode || ""}
                        onChange={(_e, val) => setEditValues(v => ({ ...v, euroCode: val || "" }))}
                        autoFocus={true}
                        borderless={false}
                    />
                ) : (
                    <span className="eurocode__cell-text" onClick={() => startEdit(row)}>
                       {row.euroCode}
                    </span>
                )
        },
        {
            key: "registrationDate",
            name: "Registreringsdato",
            fieldName: "registrationDate",
            minWidth: 160,
            maxWidth: 220,
            onRender: (row: EurocodeException) =>
                editingId === row.id ? (
                    <CustomDatePicker
                        short={true}
                        date={new Date(editValues.registrationDate || row.registrationDate)}
                        onDateChange={date =>
                            setEditValues(v => ({
                                ...v,
                                registrationDate: date ? date.toISOString() : v.registrationDate
                            }))
                        }
                    />
                ) : (
                    <span className="eurocode__cell-text" onClick={() => startEdit(row)}>
                        {formatDate(row.registrationDate)}
                    </span>
                )
        },
        {
            key: "bookingsAllowedAfterRegistration",
            name: "Bookings tilladt efter indregistreringsdato",
            fieldName: "bookingsAllowedAfterRegistration",
            minWidth: 280,
            maxWidth: 280,
            onRender: (row: EurocodeException) => (
                <Checkbox
                    checked={
                        editingId === row.id
                            ? editValues.bookingsAllowedAfterRegistration
                            : row.bookingsAllowedAfterRegistration
                    }
                    onChange={(_e, checked) => {
                        if (editingId === row.id) {
                            setEditValues(v => ({ ...v, bookingsAllowedAfterRegistration: !!checked }));
                        } else {
                            dispatch(saveEurocodeException({ ...row, bookingsAllowedAfterRegistration: !!checked }) as any);
                        }
                    }}
                />
            )
        },
        {
            key: "actions",
            name: "",
            fieldName: "actions",
            minWidth: 80,
            maxWidth: 80,
            onRender: (row: EurocodeException) =>
                editingId === row.id ? (
                    <div className="eurocode__actions-cell">
                        <IconButton iconProps={{ iconName: "CheckMark" }} onClick={confirmEdit} />
                        <IconButton iconProps={{ iconName: "Cancel" }} onClick={cancelEdit} />
                    </div>
                ) : (
                    <IconButton
                        iconProps={{ iconName: "Delete" }}
                        onClick={() => deleteRow(row.id)}
                    />
                )
        }
    ];

    return (
        <div className="eurocode__container">
            <div className="eurocode__header">
                <h2>Undtagelser</h2>
                <PrimaryButton onClick={addNew} iconProps={{ iconName: "Add" }}>
                    Opret ny
                </PrimaryButton>
            </div>
            <DetailsList
                items={exceptions}
                columns={columns}
                layoutMode={DetailsListLayoutMode.justified}
                checkboxVisibility={CheckboxVisibility.hidden}
                getKey={(item: EurocodeException) => item.id}
            />
        </div>
    );
};

export default EurocodeExceptions;
